import {
  Badge,
  Box,
  Center,
  Flex,
  IconButton,
  Spinner,
  Text
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { CloseIcon, DeleteIcon } from "@chakra-ui/icons";
import { FiSave, FiX } from "react-icons/fi";
import { MailingTableRow } from "components/mailing-components/mailing-table";
import { onElementInserted, useAnimation } from "hooks/use-animation";
import {
  State,
  StateProps
} from "components/pipeline-components/classes/state-machine/state";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { dropdownEspecies } from "components/atendimentos-components/atendimento-form/fields-data";
import { especieCodeListConsignaveis } from "components/mailing-components/mailing-modal-filter/consts";
import { getBlockDefinition, getDefaultNodeLabel } from "../node-catalog";
import { StartEndEditor } from "./side-panel-states-editors/StartEndEditor";
import { TelefoniaEditor } from "./side-panel-states-editors/TelefoniaEditor";
import { HigienizacaoOfflineEditor } from "./side-panel-states-editors/HigienizacaoOfflineEditor";
import { AnaliseIaEditor } from "./side-panel-states-editors/AnaliseIaEditor";
import { EnviaAtendimentoEditor } from "./side-panel-states-editors/EnviaAtendimentoEditor";
import { RefinEditor } from "./side-panel-states-editors/RefinEditor";
import { SaqueComplementarEditor } from "./side-panel-states-editors/SaqueComplementarEditor";
import { DigitacaoEditor } from "./side-panel-states-editors/DigitacaoEditor";
import { SmsWhatsEditor } from "./side-panel-states-editors/SmsWhatsEditor";
import { WhatsEditor } from "./side-panel-states-editors/WhatsEditor";
import { FiltroEditor } from "./side-panel-states-editors/FiltroEditor";
import { DiagnosticPanel } from "./side-panel-states-editors/DiagnosticPanel";

const SIDE_PANEL_WIDTH = 420;
const SIDE_PANEL_ANIMATION_DURATION = 150;

export type PipelineSidePanelProps = {
  isLoading: boolean;
  pipelineData?: StatesMachine;
  mailingData?: Partial<MailingTableRow>;
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
  selectedStateId?: number;
  onClearSelection?: () => void;
  midiaFileWpp?: File;
  onMidiaFileWppChange?: (file: File | undefined) => void;
};

export const PipelineSidePanel = ({
  isLoading,
  pipelineData,
  mailingData,
  updatePipelineData,
  selectedStateId,
  onClearSelection,
  midiaFileWpp,
  onMidiaFileWppChange
}: PipelineSidePanelProps) => {
  const { start } = useAnimation();

  const selectedState = selectedStateId
    ? pipelineData?.getState(selectedStateId)
    : undefined;

  const hasSelectedState = !!selectedState;
  const lastSelectedState = useRef<State | undefined>(selectedState);

  const [isPanelMounted, setIsPanelMounted] = useState(hasSelectedState);
  const panelId = "pipeline-side-panel";

  const onOpen = useCallback(async () => {
    setIsPanelMounted(true);
    const target = (await onElementInserted(`#${panelId}`)) as HTMLDivElement;

    if (!target) return;

    start({
      onUpdate(p) {
        target.style.opacity = p.toString();
        target.style.width = `${p * SIDE_PANEL_WIDTH}px`;
      },
      duration: SIDE_PANEL_ANIMATION_DURATION,
      from: 0,
      to: 1
    });
  }, [start]);

  const onClose = useCallback(async () => {
    const target = (await onElementInserted(`#${panelId}`)) as HTMLDivElement;

    if (!target) return;

    start({
      onUpdate(p) {
        target.style.opacity = p.toString();
        target.style.width = `${p * SIDE_PANEL_WIDTH}px`;
      },
      duration: SIDE_PANEL_ANIMATION_DURATION,
      from: 1,
      to: 0,
      onComplete() {
        setIsPanelMounted(false);
        lastSelectedState.current = undefined;
      }
    });
  }, [start]);

  useEffect(() => {
    if (hasSelectedState) onOpen();
    else onClose();

    lastSelectedState.current = selectedState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelectedState, onClose, onOpen, selectedStateId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClearSelection?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClearSelection]);

  const isPipelineCreated = !!pipelineData?.id;
  const isEditorDisabled = isPipelineCreated;

  const guardedUpdatePipelineData = useCallback(
    (updater: (draft: StatesMachine) => StatesMachine | void) => {
      if (isEditorDisabled) return;
      updatePipelineData(updater);
    },
    [isEditorDisabled, updatePipelineData]
  );

  const currentState = selectedState ?? lastSelectedState.current;

  if (!hasSelectedState && !isPanelMounted) return null;

  const selectedOperation = currentState?.operation;

  const selectedOperationLabel = selectedOperation
    ? getDefaultNodeLabel(selectedOperation, mailingData?.origem).title
    : undefined;
  const selectedDefinition = selectedOperation
    ? getBlockDefinition(selectedOperation)
    : undefined;

  const refinStates =
    pipelineData?.states.filter(
      (state) => state.operation === "REFIN" && state.stateType === "EXECUTOR"
    ) || [];

  const refinValue = {
    bancos: refinStates.map((state) => state.banco).filter(Boolean) as string[],
    convenio: refinStates[0]?.convenio || "",
    codigoTabela: refinStates[0]?.codigoTabela || ""
  };

  const saqueStates =
    pipelineData?.states.filter(
      (state) =>
        state.operation === "SAQUE_COMPLEMENTAR" &&
        state.stateType === "EXECUTOR"
    ) || [];

  const saqueValue = {
    banco: saqueStates[0]?.banco || "",
    idConvenio: saqueStates[0]?.idConvenio || "",
    tipoCartao: saqueStates[0]?.tipoCartao || "",
    codigoTabela: saqueStates[0]?.codigoTabela || ""
  };

  const digitacaoState = pipelineData?.getExecutorState("DIGITACAO");
  const smsState = pipelineData?.getExecutorState("SMS");
  const whatsState = pipelineData?.getExecutorState("WHATS");
  const telefoniaState = pipelineData?.getExecutorState("TELEFONIA");
  const telefoniaApi =
    (telefoniaState?.configJson?.getFilterValue("api") as string | undefined) ||
    "API_TELEFONIA";

  const bancoOperacaoOptions = especieCodeListConsignaveis.map((cod) => ({
    name: dropdownEspecies.find(({ value }) => value === cod)?.name,
    value: cod
  }));

  const canDeleteSelected =
    !!selectedState && !["START", "END"].includes(selectedState.operation);

  const removeSelectedState = () => {
    if (!selectedState || isEditorDisabled) return;

    guardedUpdatePipelineData((draft) => {
      draft.removeOperation(selectedState.operation);
      return;
    });

    onClearSelection?.();
  };

  const isStageFinished = !!pipelineData?.statusByState?.get(selectedStateId!)
    ?.finished;

  return (
    <Flex
      id={panelId}
      flexDir="column"
      justifyContent="space-between"
      pos="absolute"
      w={`${SIDE_PANEL_WIDTH}px`}
      minW="0"
      borderLeftWidth="1px"
      borderLeftStyle="solid"
      borderColor="#D6DCE6"
      // bg="rgba(248, 250, 252, 0.6)"
      backdropFilter="blur(10px)"
      overflow="auto"
      h="100%"
      pointerEvents={hasSelectedState ? "auto" : "none"}
      right={0}
      zIndex={11}
    >
      {isLoading ? (
        <Center w="100%" h="100%">
          <Spinner />
        </Center>
      ) : (
        <Flex flexDir="column" h="100%">
          {/* Header */}
          <Box
            px="20px"
            pt="18px"
            pb="16px"
            bg="white"
            borderBottom="1px solid"
            borderColor="#E2E8F0"
          >
            {pipelineData?.id != null && (
              <Flex
                mb="12px"
                align="center"
                gap="8px"
                p="8px 12px"
                borderRadius="10px"
                bg={isStageFinished ? "green.50" : "yellow.50"}
                border="1px solid"
                borderColor={isStageFinished ? "green.200" : "yellow.200"}
              >
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                  Status:
                </Text>
                <Badge
                  colorScheme={isStageFinished ? "green" : "yellow"}
                  borderRadius="full"
                  px="10px"
                  py="1px"
                  fontSize="xs"
                >
                  {isStageFinished ? "Finalizado" : "Em Andamento"}
                </Badge>
              </Flex>
            )}

            <Flex align="flex-start" justify="space-between" gap="12px">
              <Flex align="flex-start" gap="14px" flex={1} minW={0}>
                {selectedDefinition && (
                  <Flex
                    w="48px"
                    h="48px"
                    borderRadius="14px"
                    bg={selectedDefinition.accentColor}
                    align="center"
                    justify="center"
                    flexShrink={0}
                    boxShadow={`0 4px 12px 0 ${selectedDefinition.accentColor}55`}
                  >
                    <selectedDefinition.icon size={22} color="white" />
                  </Flex>
                )}
                <Box pt="2px" minW={0}>
                  <Text
                    fontWeight="800"
                    color="#1E293B"
                    fontSize="17px"
                    lineHeight="1.2"
                    noOfLines={1}
                  >
                    {selectedOperationLabel}
                  </Text>
                  <Text
                    color="#90a1b9"
                    fontWeight="500"
                    fontSize="12px"
                    mt="3px"
                    noOfLines={2}
                  >
                    {selectedDefinition?.subtitle}
                  </Text>
                </Box>
              </Flex>

              <Flex gap="4px" flexShrink={0} mt="-2px">
                {canDeleteSelected && (
                  <IconButton
                    aria-label="Remover nó"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    isDisabled={isEditorDisabled}
                    onClick={removeSelectedState}
                    borderRadius="10px"
                  />
                )}
                <IconButton
                  aria-label="Fechar painel"
                  icon={<CloseIcon w="2.5" h="2.5" />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onClearSelection?.()}
                  color="#90a1b9"
                  _hover={{ color: "#334155", bg: "#F1F5F9" }}
                  borderRadius="10px"
                />
              </Flex>
            </Flex>
          </Box>

          {/* Scrollable content */}
          <Box flex={1} overflow="auto" p="16px">
            <NodeEditor
              selectedOperation={selectedOperation}
              pipelineData={pipelineData}
              updatePipelineData={guardedUpdatePipelineData}
              isDisabled={isEditorDisabled}
              refinValue={refinValue}
              saqueValue={saqueValue}
              digitacaoState={digitacaoState}
              smsState={smsState}
              whatsState={whatsState}
              telefoniaApi={telefoniaApi}
              bancoOperacaoOptions={bancoOperacaoOptions}
              midiaFileWpp={midiaFileWpp}
              onMidiaFileWppChange={onMidiaFileWppChange}
              mailingData={mailingData}
              selectedStateId={selectedStateId}
              diagnosticUpdatePipelineData={updatePipelineData}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

function NodeEditor({
  selectedOperation,
  pipelineData,
  updatePipelineData,
  isDisabled,
  refinValue,
  saqueValue,
  digitacaoState,
  smsState,
  whatsState,
  telefoniaApi,
  bancoOperacaoOptions,
  midiaFileWpp,
  onMidiaFileWppChange,
  mailingData,
  selectedStateId,
  diagnosticUpdatePipelineData
}: {
  selectedOperation?: StateProps["operation"];
  pipelineData?: StatesMachine;
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
  isDisabled: boolean;
  refinValue: { bancos?: string[]; convenio?: string; codigoTabela?: string };
  saqueValue: {
    banco: string;
    idConvenio: string;
    tipoCartao: string;
    codigoTabela: string;
  };
  digitacaoState?: State;
  smsState?: State;
  whatsState?: State;
  telefoniaApi: string;
  bancoOperacaoOptions: { name?: string; value: string | number }[];
  midiaFileWpp?: File;
  onMidiaFileWppChange?: (file: File | undefined) => void;
  mailingData: Partial<MailingTableRow> | undefined;
  selectedStateId?: number;
  diagnosticUpdatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
}) {
  if (!selectedOperation) return null;

  if (selectedOperation === "START" || selectedOperation === "END") {
    return (
      <StartEndEditor
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        operation={selectedOperation}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "TELEFONIA") {
    return (
      <TelefoniaEditor
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "HIGIENIZACAO_OFFLINE") {
    return (
      <HigienizacaoOfflineEditor
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "ANALISE_IA") {
    return (
      <AnaliseIaEditor
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
        mailingData={mailingData}
        updatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "ENVIA_ATENDIMENTO") {
    return (
      <EnviaAtendimentoEditor
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "REFIN") {
    return (
      <RefinEditor
        isDisabled={isDisabled}
        refinValue={refinValue}
        pipelineData={pipelineData}
        updatePipelineData={updatePipelineData}
        selectedStateId={selectedStateId}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "SAQUE_COMPLEMENTAR") {
    return (
      <SaqueComplementarEditor
        isDisabled={isDisabled}
        saqueValue={saqueValue}
        updatePipelineData={updatePipelineData}
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "DIGITACAO") {
    return (
      <DigitacaoEditor
        isDisabled={isDisabled}
        digitacaoState={digitacaoState}
        updatePipelineData={updatePipelineData}
        mailingData={mailingData}
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "SMS") {
    return (
      <SmsWhatsEditor
        selectedOperation={selectedOperation}
        isDisabled={isDisabled}
        smsState={smsState}
        updatePipelineData={updatePipelineData}
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "WHATS") {
    return (
      <WhatsEditor
        isDisabled={isDisabled}
        whatsState={whatsState}
        updatePipelineData={updatePipelineData}
        midiaFileWpp={midiaFileWpp}
        onMidiaFileWppChange={onMidiaFileWppChange}
        selectedStateId={selectedStateId}
        pipelineData={pipelineData}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  if (selectedOperation === "FILTRO") {
    return (
      <FiltroEditor
        isDisabled={isDisabled}
        pipelineData={pipelineData}
        updatePipelineData={updatePipelineData}
        bancoOperacaoOptions={bancoOperacaoOptions}
        selectedStateId={selectedStateId}
        diagnosticUpdatePipelineData={diagnosticUpdatePipelineData}
      />
    );
  }

  return (
    <>
      <Text color="#90a1b9" fontWeight="600" fontSize="sm">
        Sem configurações para este tipo de nó.
      </Text>
      {selectedStateId != null && pipelineData && (
        <DiagnosticPanel
          selectedStateId={selectedStateId}
          pipelineData={pipelineData}
          operation={selectedOperation}
          updatePipelineData={diagnosticUpdatePipelineData}
        />
      )}
    </>
  );
}
